import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  TextField,
  Typography,
} from '@mui/material';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useDeviceToken from '../../../hooks/useDeviceToken';
import useDeviceTypeRequest from '../../../hooks/useDeviceTypeRequest';
import useHttp from '../../../hooks/useHttp';
import { useAuth } from '../../../context/authContext';
import FunZoneLogo from './FunZoneLogo';

// Types an employee may ask for from this screen; "private" needs no approval.
const REQUESTABLE_DEVICE_TYPES = ['company', 'counter'];

const DeviceConfirmation = ({ allowedDeviceTypes, onDeviceConfirmed }) => {
  const [selectedDeviceType, setSelectedDeviceType] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const {
    createDeviceToken,
    loginWithDeviceToken,
    isLoading: deviceTokenLoading,
    error: apiError,
  } = useDeviceToken();
  const { isLoading: httpLoading, error: httpError } = useHttp();
  const [performingLogin, setPerformingLogin] = useState(false);
  const { requestDeviceType } = useDeviceTypeRequest();
  const authCtx = useAuth();
  const [requestSent, setRequestSent] = useState(null);
  const [requestReason, setRequestReason] = useState('');
  const [isRequesting, setIsRequesting] = useState(false);

  const isLoading = deviceTokenLoading || httpLoading || performingLogin;

  const deviceTypeLabels = {
    company: 'Firmengerät',
    counter: 'Kassensystem',
    private: 'Privates Gerät',
  };

  // Types this device is not (yet) cleared for. Until it is registered the backend
  // only offers "private", which used to dead-end here — now the same screen files
  // the request that gets it registered.
  const requestableTypes = REQUESTABLE_DEVICE_TYPES.filter(
    type => !(allowedDeviceTypes || []).includes(type)
  );

  // File a device-type request for this browser. The returned claim ticket is kept
  // so the device can exchange it for a real device token after the approval.
  const handleRequestDeviceType = async deviceType => {
    setError('');
    setIsRequesting(true);
    try {
      const res = await requestDeviceType({
        requestedDeviceType: deviceType,
        reason: requestReason.trim() || undefined,
        authToken: authCtx.tempToken,
      });
      if (res?.requestDeviceToken) {
        localStorage.setItem('requestDeviceToken', res.requestDeviceToken);
      }
      setRequestSent(deviceType);
    } catch (err) {
      setError(err?.message || 'Die Anfrage konnte nicht gesendet werden.');
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDeviceConfirmation = async () => {
    if (!selectedDeviceType) {
      setError('Bitte wähle einen Gerätetyp aus.');
      return;
    }
    setPerformingLogin(true);

    setError('');

    try {
      if (selectedDeviceType === 'private') {
        localStorage.setItem('isPrivateDevice', 'true');

        if (onDeviceConfirmed) {
          onDeviceConfirmed(null); // null indicates private device
        }
        navigate('/welcome');
        return;
      }

      // Create device token for non-private devices
      const deviceTokenResult = await createDeviceToken(selectedDeviceType);

      // Complete login using temp auth token + selected device token
      const loginResult = await loginWithDeviceToken(deviceTokenResult.deviceToken);

      if (onDeviceConfirmed) {
        onDeviceConfirmed(
          deviceTokenResult.deviceToken,
          loginResult.token,
          loginResult.intermediateToken
        );
      }

      navigate('/welcome');
    } catch (err) {
      setError(err.message);
    } finally {
      setPerformingLogin(false);
    }
  };

  return (
    <Grid
      container
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: '600px',
        width: '95%',
      }}
      rowSpacing={2}
    >
      {/* LOGO */}
      <Grid item xs={12} style={{ marginBottom: '20px' }}>
        <FunZoneLogo />
      </Grid>

      {/* TITLE */}
      <Grid item xs={12}>
        <Typography variant='h5' component='h2' align='center' sx={{ mb: 2 }}>
          Gerät bestätigen
        </Typography>
      </Grid>

      {/* DESCRIPTION */}
      <Grid item xs={12}>
        <Typography variant='body1' align='center' sx={{ mb: 3, color: 'text.secondary' }}>
          Wähle den Typ dieses Geräts aus. Dies stellt sicher, dass nur autorisierte Geräte für
          bestimmte Funktionen verwendet werden können. Zum Beispiel können Kassen-Systeme nur von
          registrierten Kassen-PCs aus bedient werden.
        </Typography>
      </Grid>

      {/* DEVICE TYPE SELECTION */}
      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel>Gerätetyp auswählen</InputLabel>
          <Select
            value={selectedDeviceType}
            onChange={e => setSelectedDeviceType(e.target.value)}
            disabled={isLoading}
            label='Gerätetyp auswählen'
          >
            {allowedDeviceTypes.map(deviceType => (
              <MenuItem key={deviceType} value={deviceType}>
                {deviceTypeLabels[deviceType] || deviceType}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* ERROR ALERT */}
      {(error || apiError || httpError) && (
        <Grid item xs={12}>
          <Alert severity='error'>{error || apiError || httpError}</Alert>
        </Grid>
      )}

      {/* BUTTONS */}
      <Grid item xs={12} sx={{ marginTop: '20px' }} container>
        {/* CONFIRM BUTTON */}
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            type='button'
            onClick={handleDeviceConfirmation}
            disabled={!selectedDeviceType || isLoading}
            sx={{ maxWidth: '200px' }}
          >
            {isLoading ? 'Wird bestätigt...' : 'Bestätigen'}
            <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '5px' }} />
          </Button>
        </Grid>
      </Grid>

      {/* REQUEST A DEVICE TYPE */}
      {requestableTypes.length > 0 && (
        <Grid item xs={12} sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {requestSent ? (
            <Alert severity='success'>
              Anfrage als <strong>{deviceTypeLabels[requestSent] || requestSent}</strong> gesendet.
              Der Lead Engineer entscheidet darüber; du kannst dich solange als privates Gerät
              anmelden. Sobald die Freigabe da ist, wird dieses Gerät beim nächsten Anmelden
              automatisch übernommen.
            </Alert>
          ) : (
            <>
              <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
                Gerät ist noch nicht registriert?
              </Typography>
              <Typography variant='body2' sx={{ mb: 1.5, color: 'text.secondary' }}>
                Frag die Registrierung hier direkt an. Der Lead Engineer bekommt deine Anfrage per
                E-Mail und kann sie mit einem Klick freigeben.
              </Typography>
              <TextField
                fullWidth
                size='small'
                value={requestReason}
                onChange={e => setRequestReason(e.target.value)}
                placeholder='Optional: Wofür brauchst du das Gerät?'
                inputProps={{ maxLength: 200 }}
                disabled={isRequesting}
                sx={{ mb: 1.5 }}
              />
              <Grid container spacing={1}>
                {requestableTypes.map(deviceType => (
                  <Grid item xs={12} sm={6} key={deviceType}>
                    <Button
                      fullWidth
                      variant='outlined'
                      onClick={() => handleRequestDeviceType(deviceType)}
                      disabled={isRequesting || isLoading}
                    >
                      Als {deviceTypeLabels[deviceType] || deviceType} anfragen
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Grid>
      )}

      {/* SKIP BUTTON */}
      <Grid item xs={12} sx={{ marginTop: '10px', textAlign: 'center' }}>
        <Button
          type='button'
          variant='text'
          size='small'
          onClick={() => {
            if (onDeviceConfirmed) {
              onDeviceConfirmed(null); // null indicates private device
            }
            navigate('/welcome');
          }}
          disabled={isLoading}
          sx={{ color: 'text.secondary' }}
        >
          Überspringen (privates Gerät)
        </Button>
      </Grid>
    </Grid>
  );
};

export default DeviceConfirmation;
